import { useState } from 'react'

import { useForm } from 'react-hook-form'

import useLocalStorage from 'hooks/use-local-storage'
import { useMounted } from 'hooks/use-mounted'
import { pushToUserlist } from 'lib/utils/userlist'
import { toastError } from 'store/toasts.state'

import Box from 'components/atomic/atoms/box'
import Button from 'components/atomic/atoms/button'
import InputGroup from 'components/atomic/molecules/input-group'

type Segment = 'email-list'

const SharedSubscribeForm = ({
  label,
  segment,
}: {
  label: string
  segment: Segment
}) => {
  const mounted = useMounted()
  const [value, setValue] = useLocalStorage<boolean>(
    getSubscribeFormSegment(segment),
    false,
  )
  const [subscribing, setSubscribing] = useState(false)
  const [subscribed, setSubscribed] = useState(false)
  const { register, handleSubmit } = useForm({
    defaultValues: {
      email: '',
    },
  })

  const onSubmit = handleSubmit(async ({ email }) => {
    setSubscribing(true)
    const response = await pushToUserlist({
      email,
      properties: { [segment]: true },
    })

    if (response) {
      setSubscribed(true)
      setValue(true)
    } else {
      toastError()
    }

    setSubscribing(false)
  })

  return (
    <Box
      relative
      css={{
        mt: '$6',
        border: '1px solid $bg3',
        borderRadius: '$md',
        p: '$4',
        textAlign: 'left',
        '@sm': { minWidth: 420 },
        '.dark &': {
          bgc: '$bg2',
        },
      }}
    >
      <Box
        as="form"
        onSubmit={onSubmit}
        relative
        css={{ width: '$full', '@sm': { width: 'unset' } }}
      >
        <InputGroup.Root
          css={{
            spaceY: 2,
          }}
        >
          <InputGroup.Label
            htmlFor="email"
            css={{
              color: '$text1',
            }}
          >
            {label}
          </InputGroup.Label>
          <Box
            flex={{ '@initial': 'column', '@sm': true }}
            css={{
              gap: '$4',
              transition: 'opacity .35s ease',
              opacity: mounted ? 1 : 0,
            }}
          >
            {subscribed || (value && mounted) ? (
              <Button
                variant="secondary"
                width="full"
                disabled
                css={{
                  '&:disabled': { bgc: '$bg3', filter: 'opacity(1)' },
                }}
              >
                Subscribed, thanks!
              </Button>
            ) : (
              <>
                <InputGroup.Input
                  id="email"
                  placeholder="Type your email address"
                  css={{ '@sm': { minWidth: 250 } }}
                  {...register('email', { required: true })}
                />
                <Button type="submit" isLoading={subscribing}>
                  Subscribe
                </Button>
              </>
            )}
          </Box>
        </InputGroup.Root>
      </Box>
    </Box>
  )
}

export const getSubscribeFormSegment = (segment: Segment) =>
  `subscription-to-${segment}`

export default SharedSubscribeForm
